/* eslint-disable camelcase */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThreadMessage } from '@screentone/addon-auth-wrapper';
import { Button, Group, IconLock, Loader, Typography } from '@screentone/core';

import { ConfirmationDialog } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { FragmentStateData } from 'contexts/page-lock/PageLockContext';
import { usePagePublish } from 'contexts/page-publish-dto/usePagePublish';
import { usePublicationSettings } from 'hooks/publication-settings';
import { formatInTimeZone } from 'utils/dates';
import styles from './PageLock.module.scss';

interface PageLockProps {
  publishingState: FragmentStateData;
  release: (withForce: boolean) => Promise<ThreadMessage | null>;
  lock: (withForce: boolean) => Promise<ThreadMessage | null>;
  loading: boolean;
  draftEnabled?: boolean;
  lockType: string;
}

const PageLock: FC<PageLockProps> = ({ publishingState, release, lock, loading, draftEnabled = false, lockType }) => {
  const { alertWarning } = useAlert();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleSaveDraft } = usePagePublish();
  const { data: publicationSettings } = usePublicationSettings();
  const lockTypeLowered = useMemo(() => lockType.toLowerCase(), [lockType]);
  const tookOverActionFiredRef = useRef(false);

  const handleRelease = useCallback(
    async (withForce: boolean) => {
      try {
        setShowModal(false);
        setIsLoading(true);

        await release(withForce);
      } catch (error) {
        console.error(`Error releasing ${lockTypeLowered} lock:`, error);
      } finally {
        setIsLoading(false);
      }
    },
    [release, lockTypeLowered]
  );

  const handleLock = useCallback(
    async (withForce: boolean) => {
      try {
        setShowModal(false);
        setIsLoading(true);

        await lock(withForce);
      } catch (error) {
        console.error(`Error acquiring ${lockTypeLowered} lock:`, error);
      } finally {
        setIsLoading(false);
      }
    },
    [lock, lockTypeLowered]
  );

  useEffect(() => {
    if (!publishingState.hasTakenOver) {
      // Reset the guard whenever hasTakenOver clears so a future take-over fires correctly.
      tookOverActionFiredRef.current = false;
      return;
    }

    if (!loading && publishingState.lockedBy && !tookOverActionFiredRef.current) {
      tookOverActionFiredRef.current = true;
      alertWarning(`${publishingState.lockedBy.name} has taken over the publishing rights of the ${lockTypeLowered}.`);

      // Attempt to save the draft after taking over the lock
      if (draftEnabled) {
        handleSaveDraft({ silent: true, takenOverBy: publishingState.lockedBy }).catch((error) => {
          console.error('Error saving draft after lock takeover:', error);
          alertWarning('Failed to save your changes after lock takeover. Please try saving again.');
        });
      }
    }

    // publishingState.lockedBy is an object — use .id (primitive) to avoid re-firing when only
    // the reference changes (e.g. after thread refetches with the same user data).
    // tookOverActionFiredRef guards against re-entry when handleSaveDraft (not useCallback-wrapped)
    // changes reference on each PagePublishContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftEnabled,
    handleSaveDraft,
    loading,
    lockTypeLowered,
    publishingState.hasTakenOver,
    publishingState.lockedBy?.id
  ]);

  if (loading || !publishingState.state) {
    return null;
  }

  const transferMessage = draftEnabled
    ? 'Their unsaved changes will be auto-saved and transferred to your view.'
    : 'Their unsaved changes will be lost.';

  const owner = publishingState.isLockedByMe ? 'You' : publishingState.state.user?.name;
  const dialogDescription = publishingState.isLockedByMe ? (
    <>
      Are you sure you want to release this {lockTypeLowered}? <b>Unsaved changes will be lost.</b>
    </>
  ) : (
    <>Are you sure you want to take control? {transferMessage}</>
  );

  if (!owner) {
    // Page was released
    return (
      <Button secondary onClick={() => handleLock(false)} disabled={isLoading} data-testid="claim-page-button">
        {isLoading ? <Loader size="md" /> : 'Claim'}
      </Button>
    );
  }

  const confirmationClickFn = publishingState.isLockedByMe
    ? () => handleRelease(false) // releases
    : () => handleLock(true); // claims

  const formattedDate = formatInTimeZone(
    publishingState.state.timestamp,
    publicationSettings?.publicationSetting.preferredTimezone,
    "MMM. d 'at' h:mm aa",
    { showTimezoneAbbreviation: true }
  );

  return (
    <>
      <Group direction="column" align="end" gap="none" margin={{ right: 'md' }}>
        <Group gap="xs" align="end">
          <IconLock margin={{ right: 'xs' }} />
          <Typography size="md" color="asphalt" data-testid="currently-held-by-label">
            Currently held by
          </Typography>
          <Typography size="md" color="ink" data-testid="page-lock-owner-name">
            {owner}
          </Typography>
        </Group>
        <Group gap="sm">
          <Typography size="sm" color="asphalt" data-testid="page-lock-timestamp">
            Since {formattedDate}
          </Typography>
          <Button
            tertiary
            size="sm"
            className={styles.releaseButton}
            onClick={() => setShowModal(true)}
            disabled={isLoading}
            data-testid="claim-release-button"
          >
            {isLoading ? <Loader size="md" /> : publishingState.isLockedByMe ? 'Release' : 'Claim'}
          </Button>
        </Group>
      </Group>
      {showModal && (
        <ConfirmationDialog
          description={
            <>
              This {lockTypeLowered} is currently held by <b>{owner}</b>. {dialogDescription}
            </>
          }
          isLoading={isLoading}
          onClickFunc={confirmationClickFn}
          showDialog={setShowModal}
          title={`${lockType} ${publishingState.isLockedByMe ? 'Release' : 'Claim'}`}
          confirmLabel={publishingState.isLockedByMe ? 'Release' : `Claim ${lockType}`}
        />
      )}
    </>
  );
};

export default PageLock;
